// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LinesHeader, { LinesHeaderStyles } from "@conklin/App/Components/Cart/LinesHeader";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@conklin/Overrides/Components/CartLineCardExpanded";
import { pluralize } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    const { enableVat, vatPriceDisplay } = getSettingsCollection(state).productSettings;
    return {
        cart,
        enableVat,
        vatPriceDisplay,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationProductListStyles {
    listContainer?: InjectableCss;
    itemContainer?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    productInfoGridItem?: GridItemProps;
    productDescriptionText?: TypographyPresentationProps;
    productErpNumberText?: TypographyPresentationProps;
    qtyOrderedText?: TypographyPresentationProps;
    productPrice?: ProductPriceStyles;
    subtotalGridItem?: GridItemProps;
    subtotalText?: TypographyPresentationProps;
    // XNG-Changes
    lineCountStyles?: TypographyPresentationProps;
    cartLineCardExpandedStyles?: CartLineCardExpandedStyles;
    linesHeader?: LinesHeaderStyles;
}

export const productListStyles: OnePageCheckoutConfirmationProductListStyles = {
    productImageGridItem: { width: 2 },
    productInfoGridItem: {
        width: 8,
        css: css`
            flex-direction: column;
        `,
    },
    qtyOrderedText: { size: 13, transform: "capitalize" },
    productPrice: {
        wrapper: {
            css: css`
                flex-direction: row;
                align-items: end;
            `,
        },
        priceLabelText: {
            variant: "p",
            size: 13,
            css: css`
                margin-right: 5px;
                margin-bottom: 0;
            `,
        },
        price: {
            priceText: { size: 13, weight: "normal" },
            unitOfMeasure: {
                unitOfMeasureText: { size: 13 },
            },
        },
    },
    subtotalGridItem: {
        width: 2,
        css: css`
            justify-content: end;
        `,
    },
};

const styles = productListStyles;

const OnePageCheckoutConfirmationProductList = ({ cart, enableVat, vatPriceDisplay }: Props) => {
    if (!cart || !cart.cartLines) {
        return null;
    }

    return (
        <StyledWrapper {...styles.listContainer}>
            <Typography variant="h3">
                {pluralize(cart.cartLines.length, translate("Product"), { showNumber: true })}
            </Typography>
            <LinesHeader includeActions={true} extendedStyles={styles.linesHeader} />
            {cart.cartLines.map(cartLine => {
                return (
                    <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                        <CartLineCardExpanded
                            cart={cart}
                            editable={false}
                            showSavingsAmount={false}
                            showSavingsPercent={false}
                            // XNG-Changes
                            extendedStyles={styles.cartLineCardExpandedStyles}
                            includeActions={true}
                            showAddToList={false}
                        />
                    </CartLineContext.Provider>
                );
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationProductList),
    definition: {
        displayName: "Product List",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
